import { createContext, useContext, type ReactNode } from 'react';
import type { ConsoleApi } from '../api/consoleApi';
import { bindSessionApi } from '../api/session';

const ConsoleApiContext = createContext<ConsoleApi | null>(null);

/**
 * Injects the data provider. Pages read it through `useConsoleApi()` and never
 * touch `fetch`, so the fixture → live swap happens here only.
 *
 * Binding the session core here (and in `createProvider`) is what keeps the token out of
 * React: `signIn`/`signOut`/`tokenProvider` reach the same provider the pages consume, so a
 * test that renders a page with a substitute provider gets a login screen that talks to THAT
 * provider — including one whose `effective` answer has been forged to check that the UI is a
 * mirror.
 */
export function ConsoleApiProvider({ api, children }: { api: ConsoleApi; children: ReactNode }) {
  bindSessionApi(api);
  return <ConsoleApiContext.Provider value={api}>{children}</ConsoleApiContext.Provider>;
}

export function useConsoleApi(): ConsoleApi {
  const api = useContext(ConsoleApiContext);
  if (!api) {
    throw new Error('useConsoleApi must be used inside <ConsoleApiProvider>');
  }
  return api;
}
